import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MONTHS_IT, WEEKDAYS_FULL } from "@/lib/constants";
import { Building2, Users, CalendarClock, Wallet, CheckCircle2, ArrowRight, ArrowLeft, Plus, Trash2 } from "lucide-react";
import { DurationInput, DurationFormatBadge } from "@/foundation/duration";

const STEPS = [
  { key: "company",  label: "Azienda",           icon: Building2 },
  { key: "period",   label: "Periodo",           icon: CalendarClock },
  { key: "employees",label: "Dipendenti",        icon: Users },
  { key: "profiles", label: "Profili orari",     icon: CalendarClock },
  { key: "balances", label: "Saldi iniziali",    icon: Wallet },
  { key: "done",     label: "Completato",        icon: CheckCircle2 },
];

export default function Wizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const [company, setCompany] = useState({
    name: "", tax_code: "", vat: "", address: "", email: "", phone: "", notes: "",
    compensation_period_months: 6,
    semester_start_month: 1,
    active_month: now.getMonth() + 1,
    active_year: now.getFullYear(),
  });
  const [companyId, setCompanyId] = useState(null);

  const emptyEmployee = () => ({
    first_name: "", last_name: "", fiscal_code: "", matricola: "",
    hire_date: "", qualification: "", employment_type: "full_time",
    initial_balance_hours: 0,
    // default weekly hours
    monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0,
  });
  const [employees, setEmployees] = useState([emptyEmployee()]);

  const nextStep = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  // Step 1: create company
  const saveCompany = async () => {
    if (!company.name.trim()) { toast.error("Nome azienda obbligatorio"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/companies", company);
      setCompanyId(data.id);
      toast.success("Azienda creata");
      nextStep();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const savePeriod = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${companyId}`, {
        compensation_period_months: company.compensation_period_months,
        semester_start_month: company.semester_start_month,
        active_month: company.active_month,
        active_year: company.active_year,
      });
      toast.success("Periodo di compensazione salvato");
      nextStep();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const setEmp = (idx, patch) => setEmployees((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const addEmp = () => setEmployees((prev) => [...prev, emptyEmployee()]);
  const rmEmp = (idx) => setEmployees((prev) => prev.filter((_, i) => i !== idx));

  const saveEmployeesAndProfiles = async () => {
    const valid = employees.filter((e) => e.first_name.trim() && e.last_name.trim());
    setSaving(true);
    try {
      if (valid.length === 0) {
        toast.info("Nessun dipendente inserito manualmente — potrai importarli dal Tabulato");
        setStep(STEPS.length - 1);
        return;
      }
      const effective_from = `${company.active_year}-${String(company.active_month).padStart(2, "0")}-01`;
      for (const e of valid) {
        const empRes = await api.post("/employees", {
          company_id: companyId,
          first_name: e.first_name.trim(),
          last_name: e.last_name.trim(),
          fiscal_code: e.fiscal_code || null,
          matricola: e.matricola || null,
          hire_date: e.hire_date || null,
          qualification: e.qualification || null,
          employment_type: e.employment_type,
          initial_balance_hours: Number(e.initial_balance_hours) || 0,
          initial_balance_month: company.active_month,
          initial_balance_year: company.active_year,
          active: true,
        });
        const empId = empRes.data.id;
        await api.post("/hour-profiles", {
          employee_id: empId,
          name: "Standard",
          effective_from,
          monday: Number(e.monday) || 0,
          tuesday: Number(e.tuesday) || 0,
          wednesday: Number(e.wednesday) || 0,
          thursday: Number(e.thursday) || 0,
          friday: Number(e.friday) || 0,
          saturday: Number(e.saturday) || 0,
          sunday: Number(e.sunday) || 0,
        });
      }
      toast.success(`${valid.length} dipendenti creati con profilo orario`);
      setStep(STEPS.length - 1);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Configurazione iniziale</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-slate-900" style={{ fontFamily: "Manrope" }}>
          Primo avvio — Setup azienda
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Configura in pochi passi l'azienda, i dipendenti, i profili orari e i saldi banca ore di partenza.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap gap-2 items-center">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm border ${
                  active ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                  : done ? "border-emerald-200 bg-white text-emerald-700"
                  : "border-slate-200 bg-white text-slate-500"
                }`}
                data-testid={`wizard-step-${s.key}`}
              >
                <Icon size={16} />
                <span className="font-medium">{i + 1}. {s.label}</span>
              </div>
              {i < STEPS.length - 1 && <ArrowRight size={14} className="text-slate-300" />}
            </div>
          );
        })}
      </div>

      <Card className="border-slate-200 rounded-md">
        <CardContent className="p-6">
          {/* STEP 0: Company */}
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Anagrafica azienda</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Ragione sociale *</Label><Input value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="wiz-company-name" /></div>
                <div><Label>Codice fiscale</Label><Input value={company.tax_code} onChange={(e) => setCompany({ ...company, tax_code: e.target.value })} data-testid="wiz-company-cf" /></div>
                <div><Label>Partita IVA</Label><Input value={company.vat} onChange={(e) => setCompany({ ...company, vat: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} /></div>
                <div><Label>Indirizzo</Label><Input value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Note</Label><Textarea value={company.notes} onChange={(e) => setCompany({ ...company, notes: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={saveCompany} disabled={saving} data-testid="wiz-next-company" className="bg-emerald-800 hover:bg-emerald-700 text-white">
                  Avanti <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 1: Period */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Periodo di compensazione</h2>
              <p className="text-sm text-slate-500">Configura il periodo semestrale e il mese di partenza attivo.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Durata periodo (mesi)</Label>
                  <Select value={String(company.compensation_period_months)} onValueChange={(v) => setCompany({ ...company, compensation_period_months: Number(v) })}>
                    <SelectTrigger data-testid="wiz-period-length"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 (trimestrale)</SelectItem>
                      <SelectItem value="6">6 (semestrale)</SelectItem>
                      <SelectItem value="12">12 (annuale)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mese inizio semestre</Label>
                  <Select value={String(company.semester_start_month)} onValueChange={(v) => setCompany({ ...company, semester_start_month: Number(v) })}>
                    <SelectTrigger data-testid="wiz-semester-start"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mese attivo</Label>
                  <Select value={String(company.active_month)} onValueChange={(v) => setCompany({ ...company, active_month: Number(v) })}>
                    <SelectTrigger data-testid="wiz-active-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Anno attivo</Label>
                  <Input type="number" value={company.active_year} onChange={(e) => setCompany({ ...company, active_year: Number(e.target.value) })} />
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={prevStep}><ArrowLeft size={16} className="mr-2" /> Indietro</Button>
                <Button onClick={savePeriod} disabled={saving} data-testid="wiz-next-period" className="bg-emerald-800 hover:bg-emerald-700 text-white">
                  Avanti <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: Employees */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Dipendenti</h2>
                <Button variant="outline" onClick={addEmp} data-testid="wiz-add-employee"><Plus size={16} className="mr-1" /> Aggiungi</Button>
              </div>
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                <b>Suggerimento:</b> se hai un file <i>Tabulato semestrale</i> del tuo studio paghe puoi importarlo dopo il wizard
                per creare automaticamente dipendenti, profili orari e ratei. In questo caso puoi saltare questi passi lasciando l'elenco vuoto.
              </div>
              <div className="space-y-4">
                {employees.map((e, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-md p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Dipendente #{idx + 1}</div>
                      {employees.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => rmEmp(idx)} className="text-red-700 hover:bg-red-50">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div><Label>Cognome *</Label><Input value={e.last_name} onChange={(ev) => setEmp(idx, { last_name: ev.target.value })} data-testid={`wiz-emp-lastname-${idx}`} /></div>
                      <div><Label>Nome *</Label><Input value={e.first_name} onChange={(ev) => setEmp(idx, { first_name: ev.target.value })} data-testid={`wiz-emp-firstname-${idx}`} /></div>
                      <div><Label>Matricola</Label><Input value={e.matricola} onChange={(ev) => setEmp(idx, { matricola: ev.target.value })} /></div>
                      <div><Label>Codice fiscale</Label><Input value={e.fiscal_code} onChange={(ev) => setEmp(idx, { fiscal_code: ev.target.value })} /></div>
                      <div><Label>Qualifica</Label><Input value={e.qualification} onChange={(ev) => setEmp(idx, { qualification: ev.target.value })} /></div>
                      <div>
                        <Label>Tipo</Label>
                        <Select value={e.employment_type} onValueChange={(v) => setEmp(idx, { employment_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full_time">Full time</SelectItem>
                            <SelectItem value="part_time">Part time</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Assunzione</Label><Input type="date" value={e.hire_date} onChange={(ev) => setEmp(idx, { hire_date: ev.target.value })} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={prevStep}><ArrowLeft size={16} className="mr-2" /> Indietro</Button>
                <Button onClick={nextStep} data-testid="wiz-next-employees" className="bg-emerald-800 hover:bg-emerald-700 text-white">
                  Avanti <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Profiles */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Profili orari settimanali</h2>
              <p className="text-sm text-slate-500">Ore teoriche per ogni giorno della settimana (0 = giorno di riposo).</p>
              <div className="space-y-3">
                {employees.map((e, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-md p-4">
                    <div className="text-sm font-semibold mb-3 flex items-center justify-between gap-2">{e.last_name || "—"} {e.first_name}<DurationFormatBadge data-testid={`wiz-profile-${idx}-format-badge`} /></div>
                    <div className="grid grid-cols-7 gap-2">
                      {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((day, di) => (
                        <div key={day}>
                          <Label className="text-xs">{WEEKDAYS_FULL[di]}</Label>
                          <DurationInput value={e[day]} onCommit={(n) => setEmp(idx, { [day]: n ?? 0 })} data-testid={`wiz-profile-${idx}-${day}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={prevStep}><ArrowLeft size={16} className="mr-2" /> Indietro</Button>
                <Button onClick={nextStep} data-testid="wiz-next-profiles" className="bg-emerald-800 hover:bg-emerald-700 text-white">
                  Avanti <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 4: Balances */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Saldo banca ore iniziale</h2>
              <p className="text-sm text-slate-500">
                Saldo da riportare all'inizio del mese <b>{MONTHS_IT[company.active_month - 1]} {company.active_year}</b>. Usa valori negativi se il dipendente è in debito.
              </p>
              <div className="space-y-2">
                {employees.map((e, idx) => (
                  <div key={idx} className="flex items-center gap-3 border border-slate-200 rounded-md p-3">
                    <div className="flex-1 text-sm">{e.last_name} {e.first_name}</div>
                    <div className="w-40">
                      <DurationInput allowNegative value={e.initial_balance_hours}
                             onCommit={(n) => setEmp(idx, { initial_balance_hours: n ?? 0 })}
                             className="text-right"
                             data-testid={`wiz-balance-${idx}`} />
                    </div>
                    <DurationFormatBadge data-testid={`wiz-balance-${idx}-format-badge`} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={prevStep}><ArrowLeft size={16} className="mr-2" /> Indietro</Button>
                <Button onClick={saveEmployeesAndProfiles} disabled={saving} data-testid="wiz-finish" className="bg-emerald-800 hover:bg-emerald-700 text-white">
                  {saving ? "Salvataggio…" : "Completa configurazione"} <CheckCircle2 size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP DONE */}
          {step === STEPS.length - 1 && (
            <div className="text-center py-10">
              <CheckCircle2 size={56} className="text-emerald-700 mx-auto mb-4" />
              <h2 className="text-2xl font-bold" style={{ fontFamily: "Manrope" }}>Configurazione completata</h2>
              <p className="text-slate-500 mt-2 text-sm">Ora puoi iniziare a caricare le presenze mensili.</p>
              <Button onClick={() => navigate("/")} className="mt-6 bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="wiz-goto-dashboard">
                Vai alla Dashboard <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
